import { Sun, FileText, Zap, ClipboardCheck, Lightbulb, Building2, Wrench, ArrowRight } from "lucide-react";
import { CTASection } from "../components/CTASection";
import { getWhatsAppLink } from "../lib/config";

const ALL_SERVICES = [
  {
    icon: Sun,
    title: "Progettazione Impianti Fotovoltaici",
    text:
      "Progettazione completa di impianti fotovoltaici residenziali e commerciali fino a 1000 kW.",
    points: [
      "Analisi di fattibilità tecnica ed economica",
      "Dimensionamento ottimale dell'impianto",
      "Scelta dei componenti più efficienti",
      "Studi di producibilità con software dedicati",
    ],
    price: "da 250€",
  },
  {
    icon: FileText,
    title: "Gestione Pratiche GSE & E-Distribuzione",
    text: "Servizio principale: gestione completa delle pratiche amministrative e burocratiche.",
    points: [
      "Domanda di connessione E-Distribuzione (TICA)",
      "Pratiche comunali, paesaggistiche e autorizzazioni",
      "Convenzione GSE (Scambio sul Posto, RID, Ritiro Dedicato)",
      "Comunicazione Officina Elettrica e UTF",
    ],
    price: "da 250€",
    featured: true,
  },
  {
    icon: ClipboardCheck,
    title: "Conto Termico 2.0 e 3.0",
    text: "Accesso agli incentivi GSE per interventi di efficienza energetica e fonti rinnovabili.",
    points: [
      "Verifica requisiti e ammissibilità",
      "Compilazione e invio della pratica",
      "Caricamento documentazione su portale GSE",
      "Follow-up fino all'erogazione del contributo",
    ],
    price: "su preventivo",
  },
  {
    icon: Zap,
    title: "Progettazione Elettrica",
    text: "Progettazione di impianti elettrici civili, industriali e commerciali.",
    points: [
      "Progettazione impianti elettrici B.T.",
      "Quadri elettrici certificati CEI",
      "Dimensionamento impianti e calcolo carichi",
      "Dichiarazioni di conformità (DM 37/08)",
    ],
    price: "su preventivo",
  },
  {
    icon: Lightbulb,
    title: "Consulenza Energetica",
    text: "Analisi consumi, ottimizzazione delle bollette e supporto nella scelta dei fornitori.",
    points: [
      "Analisi storica consumi luce e gas",
      "Studio di risparmio personalizzato",
      "Confronto offerte mercato libero",
      "Consigli su accumulo, colonnine e pompe di calore",
    ],
    price: "personalizzato",
  },
  {
    icon: Building2,
    title: "Accumulo & Comunità Energetiche",
    text: "Soluzioni avanzate per autoconsumo intelligente e CER (Comunità Energetiche Rinnovabili).",
    points: [
      "Dimensionamento sistemi di accumulo (storage)",
      "Studio fattibilità CER e gruppi di autoconsumo",
      "Modellizzazione flussi energetici",
      "Pratiche GSE per CER",
    ],
    price: "su preventivo",
  },
  {
    icon: Wrench,
    title: "Selezione Installatori Partner",
    text:
      "Ti mettiamo in contatto con aziende installatrici di fiducia, con prezzi competitivi e materiali certificati.",
    points: [
      "Rete di installatori qualificati su tutta Italia",
      "Preventivi confrontati e trasparenti",
      "Supporto in fase di sopralluogo",
      "Verifica del corretto montaggio",
    ],
    price: "incluso nella consulenza",
  },
];

export default function ServiziPage() {
  return (
    <div data-testid="servizi-page" className="min-h-screen bg-slate-950 text-white">
      <section className="relative pt-40 pb-16">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 70% 30%, rgba(16,185,129,0.18), transparent 50%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-5">
          <div className="text-xs font-semibold tracking-[0.25em] text-emerald-400 uppercase mb-4">
            Servizi
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
            Soluzioni complete per il tuo{" "}
            <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
              impianto fotovoltaico
            </span>
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl">
            Dalla progettazione all'allaccio in rete, dalle pratiche GSE al Conto Termico:
            un unico interlocutore tecnico in tutta Italia.
          </p>
        </div>
      </section>

      <section data-testid="services-grid-section" className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ALL_SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  data-testid={`servizio-card-${i}`}
                  className={`relative p-8 rounded-3xl border flex flex-col transition-all ${
                    s.featured
                      ? "border-emerald-400/40 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 shadow-xl shadow-emerald-500/10"
                      : "border-white/5 bg-white/[0.02] hover:border-emerald-400/20"
                  }`}
                >
                  {s.featured && (
                    <span className="absolute top-5 right-5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold tracking-wider uppercase">
                      Principale
                    </span>
                  )}
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/30">
                    <Icon className="w-7 h-7 text-slate-950" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{s.title}</h3>
                  <p className="text-sm text-slate-400 mb-5 leading-relaxed">{s.text}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {s.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
                        <span className="text-emerald-400 mt-0.5">✓</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="pt-5 border-t border-white/5 flex items-center justify-between">
                    <span className="text-xs text-slate-500 uppercase tracking-wider">Prezzo</span>
                    <span className="text-sm font-bold text-emerald-400">{s.price}</span>
                  </div>
                  <a
                    href={getWhatsAppLink(`Salve, vorrei informazioni sul servizio: ${s.title}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`servizio-wa-${i}`}
                    className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-500/10 hover:bg-emerald-500 hover:text-slate-950 text-emerald-300 font-semibold text-sm transition-all"
                  >
                    Chiedi su WhatsApp
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection
        title="Hai bisogno di un preventivo personalizzato?"
        subtitle="Raccontami il tuo progetto: ti rispondo entro 24 ore con un'offerta chiara e trasparente."
        secondaryLabel="Richiedi un preventivo"
        secondaryTo="/consulenza"
      />
    </div>
  );
}
